import { useQuery } from "@tanstack/react-query";
import fetcher from "@/shared/api/fetcher";
import { axiosInstance } from "@/shared/api";
import { env } from "@/shared/config/env";
import { isCapabilityEnabled } from "@/api";
import { editorContextKeys } from "./queryKeys";
import type { EditorContext } from "../model/types";
import { readEditorMaterials } from "../model/materials";
import { readEditorCollections } from "../model/collections";

/**
 * Data hooks for the endpoints the contract request proposes. Each is gated
 * on its capability: while backend hasn't shipped the route, the query never
 * runs (no doomed request, no 404 noise) and callers get `unavailable: true`
 * to render their own "not yet" state. The interceptor gate is the backstop;
 * this is the polite layer above it.
 *
 * `isLoading` means a first fetch is in flight. TanStack Query v4 reports a
 * query that never ran — gated, or not `needed` — as loading forever, which
 * left a gated picker's spinner turning for good (dev build without
 * `collections`, 2026-11-02); `isInitialLoading` is the flag that means it.
 */
const gated = <Q extends { isInitialLoading: boolean }>(
  query: Q,
  enabled: boolean,
) => ({ ...query, isLoading: query.isInitialLoading, unavailable: !enabled });

export function useEditorContext() {
  const enabled = isCapabilityEnabled("context");
  const query = useQuery({
    queryKey: editorContextKeys.context(),
    queryFn: () =>
      fetcher<{ data: EditorContext }>("/context").then((r) => r.data),
    enabled,
    meta: { silent: true },
  });
  return gated(query, enabled);
}

export function useEditorCategories() {
  const enabled = isCapabilityEnabled("browse");
  const query = useQuery({
    queryKey: editorContextKeys.categories(),
    queryFn: () => fetcher<unknown>("/categories"),
    enabled,
    meta: { silent: true },
  });
  return gated(query, enabled);
}

/**
 * The category tree exactly as the live storefront reads it: active categories
 * only, each with its published `productCount`. The editor's own `/categories`
 * carries neither, so a preview built from it drew categories the storefront
 * hides (ClickUp 86bcr4hwq).
 *
 * The route is public and outside the editor namespace, so the URL is absolute:
 * the interceptors leave an absolute URL alone (no namespace, no editor token,
 * which this route would not accept anyway), and the store comes from
 * `x-store-id` as it does for the storefront. Without a store id the query does
 * not run, and callers treat the stock as unknown.
 */
export function useStorefrontCategoryTree(storeId: string | undefined) {
  return useQuery({
    queryKey: [...editorContextKeys.all, "storefront-categories", storeId],
    queryFn: () =>
      axiosInstance
        .get<unknown>(
          `${env.API_URL}/storefront/category-variant/categories/tree`,
          { headers: { "x-store-id": storeId } },
        )
        .then((res) => res.data),
    enabled: !!storeId,
    meta: { silent: true },
  });
}

/**
 * The store's ACTIVE materials, in the order the dashboard keeps them — the
 * same set and order `catalog.materials` resolves (backend
 * `resolveMaterials`). `data` is the parsed list; a response in any other
 * shape reads as no list at all, never as an empty one, so a contract slip
 * cannot tell a merchant their store has no materials.
 */
export function useEditorMaterials({
  /** Off for a template with nothing drawn from materials: no request. */
  needed = true,
}: { needed?: boolean } = {}) {
  const enabled = isCapabilityEnabled("materials");
  const query = useQuery({
    queryKey: editorContextKeys.materials(),
    queryFn: () => fetcher<unknown>("/materials").then(readEditorMaterials),
    enabled: enabled && needed,
    meta: { silent: true },
  });
  return gated(query, enabled);
}

/**
 * The store's product lists — defaults first, then the merchant's own — with
 * each list's page under `storeTemplateId`'s listing path. `data` is the
 * parsed list, or null for a response in any other shape.
 *
 * Refetched when the merchant comes back to the editor: lists are created,
 * filled and switched off in the dashboard, usually in another tab opened
 * from this one ("Manage product lists").
 */
export function useEditorCollections({
  storeTemplateId,
  /** Off while nothing on screen needs the lists: no request. */
  needed = true,
}: { storeTemplateId?: string; needed?: boolean } = {}) {
  const enabled = isCapabilityEnabled("collections");
  const query = useQuery({
    queryKey: editorContextKeys.collections(storeTemplateId),
    queryFn: () =>
      fetcher<unknown>(
        storeTemplateId
          ? `/collections?storeTemplateId=${encodeURIComponent(storeTemplateId)}`
          : "/collections",
      ).then(readEditorCollections),
    enabled: enabled && needed,
    refetchOnWindowFocus: true,
    meta: { silent: true },
  });
  return gated(query, enabled);
}

export function useEditorProducts(
  params: {
    search?: string;
    page?: number;
    limit?: number;
    /** `PUBLISH` for what a shopper can see; absent lists every product. */
    status?: "PUBLISH";
    /**
     * Each row also carries its variants and materials. Sent only while the
     * `productDetail` capability is on — an older backend rejects the param.
     */
    detail?: boolean;
  } = {},
  {
    /** Off while nothing on screen draws the list: no request. */
    needed = true,
  }: { needed?: boolean } = {},
) {
  const enabled = isCapabilityEnabled("browse");
  const sent = {
    ...params,
    detail: params.detail && isCapabilityEnabled("productDetail") ? true : undefined,
  };
  const search = new URLSearchParams(
    Object.entries(sent)
      .filter(([, v]) => v !== undefined && v !== null)
      .map(([k, v]) => [k, String(v)]),
  ).toString();
  const query = useQuery({
    queryKey: editorContextKeys.products(sent),
    queryFn: () => fetcher<unknown>(search ? `/products?${search}` : "/products"),
    enabled: enabled && needed,
    meta: { silent: true },
  });
  return gated(query, enabled);
}
